/**
 * Assistant de première connexion — founder onboarding (gap I2 / R1).
 *
 * Five steps, presented once to a new user (and re-openable from /today):
 *   1. Stage (exploring / building / built-no-users / selling / other) + goal
 *      (first N users or an MRR target).
 *   2. Product: site URL, App Store / Play link, or "two sentences" — with the
 *      ownership attestation the server requires (400 otherwise).
 *   3. Brief review — the extracted brief, editable (each save = a version).
 *   4. Connect a first channel (+ an OPTIONAL, skippable teammate invite).
 *   5. "Your first plan is ready" — seeds strategy + offering + brand voice from
 *      the brief, then lands on /today.
 * `completeOnboarding()` is called at the end (or on Skip) so it never shows
 * again automatically. `OnboardingGate` mounts it in the shell and fails OPEN.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { WithCreditCost } from '@/features/credits';
import { useT, tr } from '@/i18n';
import { createOrgInvitation, ORG_ROLE_LABELS, type OrgRole } from '@/lib/api/org';
import { ReadOnlyNotice } from '@/features/access/ReadOnlyNotice';
import { READ_ONLY_HINT, useRoles } from '@/routes/settings/useRoles';
import { getAuthUrl, listProviders, type ProviderInfo } from '@/lib/api/platforms';
import { completeOnboarding, getOnboardingStatus } from '@/lib/api/onboarding';
import {
  type FounderGoal,
  type FounderStage,
  type ProductBrief,
  type SeedResult,
  getBrief,
  seedFromBrief,
} from '@/lib/api/founder';
import { Badge, Button, ErrorBanner, Field, Select, Spinner, TextInput } from '@/routes/settings/ui';
import { BriefEditor, BriefSourceForm, StageGoalFields, founderErrorMessage } from '@/routes/strategy-mktg/brief-ui';

const STEP_COUNT = 5;
const INVITE_ROLES: OrgRole[] = ['admin', 'member'];

// ---------------------------------------------------------------------------
// Step 4 helpers — connect a platform + optional invite
// ---------------------------------------------------------------------------

function InviteDisclosure() {
  const { t, locale } = useT();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [inviting, setInviting] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleInvite(event: React.FormEvent) {
    event.preventDefault();
    const target = email.trim();
    if (!target) return;
    setError(null);
    setInviting(true);
    try {
      await createOrgInvitation({ email: target, org_role: role });
      setInvitedEmail(target);
      setEmail('');
    } catch (err) {
      setError(founderErrorMessage(err, t, locale));
    } finally {
      setInviting(false);
    }
  }

  return (
    <details className="rounded-token border border-border p-3" data-testid="onboarding-invite">
      <summary className="cursor-pointer text-sm font-medium text-foreground">{t('founder.onboarding.invite.toggle')}</summary>
      <div className="mt-3 space-y-3">
        {invitedEmail && (
          <div className="rounded-token border border-success/40 bg-success/10 px-3 py-2 text-sm text-success">
            {t('founder.onboarding.invite.sent', { email: invitedEmail })}
          </div>
        )}
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        <form className="space-y-3" onSubmit={handleInvite}>
          <Field label={t('founder.onboarding.invite.email')}>
            <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={tr('routes.onboarding.onboardingWizard.firstnameCompanyCom')} autoComplete="off" />
          </Field>
          <Field label={t('founder.onboarding.invite.role')}>
            <Select value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
              {INVITE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ORG_ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit" variant="secondary" disabled={inviting || !email.trim()}>
            {inviting ? t('founder.onboarding.invite.sending') : t('founder.onboarding.invite.send')}
          </Button>
        </form>
      </div>
    </details>
  );
}

function ConnectStep() {
  const { t, locale } = useT();
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // AUTHZ-3: starting an OAuth connection needs a writer role (server 403s a viewer).
  const readOnly = !useRoles().isWriter;

  useEffect(() => {
    let alive = true;
    listProviders()
      .then((list) => alive && setProviders(list))
      .catch((err) => alive && setLoadError(founderErrorMessage(err, t, locale)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleConnect(platform: string) {
    if (readOnly) return;
    setActionError(null);
    setConnecting(platform);
    try {
      const { auth_url } = await getAuthUrl(platform as ProviderInfo['platform']);
      // New tab so the wizard keeps its state; the callback returns to /platforms.
      window.open(auth_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setActionError(founderErrorMessage(err, t, locale));
    } finally {
      setConnecting(null);
    }
  }

  const configured = providers.filter((p) => p.configured);
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-foreground">{t('founder.onboarding.connect.title')}</h3>
      <p className="text-sm text-muted">{t('founder.onboarding.connect.subtitle')}</p>
      {actionError && <ErrorBanner message={actionError} onDismiss={() => setActionError(null)} />}
      <ReadOnlyNotice />
      {loadError ? (
        <ErrorBanner message={loadError} onDismiss={() => setLoadError(null)} />
      ) : loading ? (
        <Spinner label={t('founder.onboarding.connect.loading')} />
      ) : configured.length === 0 ? (
        <p className="text-sm text-muted">{t('founder.onboarding.connect.none')}</p>
      ) : (
        <ul className="space-y-2">
          {configured.map((p) => (
            <li key={p.platform} className="flex items-center justify-between gap-3 rounded-token border border-border bg-surface px-3 py-2">
              <span className="flex items-center gap-2 text-sm">
                <span className="font-medium text-foreground">{p.label}</span>
                {p.connected && <Badge className="bg-success/15 text-success">{t('founder.onboarding.connect.connected')}</Badge>}
              </span>
              <Button
                type="button"
                variant={p.connected ? 'ghost' : 'secondary'}
                onClick={() => void handleConnect(p.platform)}
                disabled={connecting === p.platform || readOnly}
                title={readOnly ? READ_ONLY_HINT : undefined}
              >
                {connecting === p.platform
                  ? t('founder.onboarding.connect.opening')
                  : p.connected
                    ? t('founder.onboarding.connect.reconnect')
                    : t('founder.onboarding.connect.connect')}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <InviteDisclosure />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — seed the first plan
// ---------------------------------------------------------------------------

function ReadyStep({ brief, onSeeded }: { brief: ProductBrief | null; onSeeded: (r: SeedResult) => void }) {
  const { t, locale } = useT();
  const [state, setState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (!brief || started.current) return;
    started.current = true;
    if (brief.seeded.strategy_id) {
      setState('done');
      return;
    }
    setState('running');
    seedFromBrief()
      .then((r) => {
        setState('done');
        onSeeded(r);
      })
      .catch((err) => {
        setState('error');
        setError(founderErrorMessage(err, t, locale));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief]);

  return (
    <div className="space-y-3" data-testid="onboarding-ready">
      <div className="text-4xl" aria-hidden="true">
        🚀
      </div>
      <h3 className="text-lg font-semibold text-foreground">{t('founder.onboarding.ready.title')}</h3>
      <p className="text-sm text-muted">{brief ? t('founder.onboarding.ready.subtitle') : t('founder.onboarding.ready.noBrief')}</p>
      {state === 'running' && <Spinner label={t('founder.onboarding.ready.seeding')} />}
      {state === 'done' && <p className="text-sm font-medium text-success">✓ {t('founder.onboarding.ready.seeded')}</p>}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The wizard (full-screen overlay)
// ---------------------------------------------------------------------------

export function OnboardingWizard({ onDone }: { onDone: () => void }) {
  const { t, locale } = useT();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [stage, setStage] = useState<FounderStage | null>(null);
  const [goal, setGoal] = useState<FounderGoal>({ metric: 'users', target: null, label: null });
  const [brief, setBrief] = useState<ProductBrief | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);

  // An existing brief (re-opened wizard) pre-fills stage / goal and step 3.
  useEffect(() => {
    let alive = true;
    getBrief()
      .then((b) => {
        if (!alive || !b) return;
        setBrief(b);
        if (b.stage) setStage(b.stage);
        if (b.goal) setGoal({ metric: b.goal.metric ?? 'users', target: b.goal.target ?? null, label: b.goal.label ?? null, currency: b.goal.currency ?? null });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  /** Mark onboarding complete, then go to /today. Fails open: the wizard always
   *  closes so the user is never trapped. */
  async function finish(goToday: boolean) {
    setFinishError(null);
    setFinishing(true);
    try {
      await completeOnboarding();
    } catch (err) {
      setFinishError(founderErrorMessage(err, t, locale));
    } finally {
      setFinishing(false);
      onDone();
      if (goToday) void navigate('/today');
    }
  }

  const isLast = step === STEP_COUNT - 1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('founder.onboarding.dialogLabel')}
      data-testid="onboarding-wizard"
      data-step={step + 1}
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-token-lg border border-border bg-surface shadow-elevation-3">
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5" aria-hidden="true">
              {Array.from({ length: STEP_COUNT }).map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? 'w-6 bg-primary' : i < step ? 'w-3 bg-primary/50' : 'w-3 bg-surface-2'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-muted">{t('founder.onboarding.stepOf', { step: step + 1, total: STEP_COUNT })}</span>
          </div>
          <button
            type="button"
            onClick={() => void finish(false)}
            disabled={finishing}
            className="rounded-token px-2 py-1 text-xs text-muted transition-colors hover:bg-surface-2 hover:text-foreground disabled:opacity-50"
            data-testid="onboarding-skip"
          >
            {t('founder.onboarding.skip')}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {step === 0 && (
            <div className="space-y-3" data-testid="onboarding-stage">
              <h3 className="text-lg font-semibold text-foreground">{t('founder.onboarding.stage.title')}</h3>
              <p className="text-sm text-muted">{t('founder.onboarding.stage.subtitle')}</p>
              <StageGoalFields stage={stage} goal={goal} onStage={setStage} onGoal={setGoal} />
            </div>
          )}
          {step === 1 && (
            <div className="space-y-3" data-testid="onboarding-product">
              <h3 className="text-lg font-semibold text-foreground">{t('founder.onboarding.product.title')}</h3>
              <p className="text-sm text-muted">{t('founder.onboarding.product.subtitle')}</p>
              <BriefSourceForm
                stage={stage}
                goal={goal}
                onExtracted={(b) => {
                  setBrief(b);
                  setStep(2);
                }}
              />
            </div>
          )}
          {step === 2 && (
            <div className="space-y-3" data-testid="onboarding-review">
              <h3 className="text-lg font-semibold text-foreground">{t('founder.onboarding.review.title')}</h3>
              <p className="text-sm text-muted">{t('founder.onboarding.review.subtitle')}</p>
              {brief ? (
                <BriefEditor brief={brief} canWrite compact onSaved={(b) => setBrief(b)} />
              ) : (
                <p className="text-sm text-muted">{t('founder.onboarding.review.none')}</p>
              )}
            </div>
          )}
          {step === 3 && <ConnectStep />}
          {step === 4 && <ReadyStep brief={brief} onSeeded={(r) => setBrief(r.brief)} />}
          {finishError && (
            <div className="mt-4">
              <ErrorBanner message={finishError} onDismiss={() => setFinishError(null)} />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          <Button type="button" variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || finishing}>
            {t('founder.onboarding.prev')}
          </Button>
          {isLast ? (
            <Button type="button" variant="primary" onClick={() => void finish(true)} disabled={finishing} data-testid="onboarding-finish">
              {finishing ? t('founder.onboarding.finishing') : t('founder.onboarding.finish')}
            </Button>
          ) : (
            // Entering the last step seeds the plan from the brief (metered
            // product_brief_seed) when it was not seeded yet: price it before the click.
            <WithCreditCost
              feature={step === STEP_COUNT - 2 && brief && !brief.seeded.strategy_id ? 'product_brief_seed' : undefined}
            >
              <Button type="button" variant="primary" onClick={() => setStep((s) => Math.min(STEP_COUNT - 1, s + 1))} data-testid="onboarding-next">
                {t('founder.onboarding.next')}
              </Button>
            </WithCreditCost>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The gate (mount in the app shell)
// ---------------------------------------------------------------------------

/**
 * Self-contained first-run gate: fetches the onboarding status once and renders
 * the wizard only if incomplete. Fails OPEN on any error.
 */
export function OnboardingGate() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let alive = true;
    getOnboardingStatus()
      .then((status) => {
        if (alive && status && status.completed === false) setShow(true);
      })
      .catch(() => {
        /* fail open — never block the app on a status hiccup */
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!show) return null;
  return <OnboardingWizard onDone={() => setShow(false)} />;
}
